import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  CheckCircle2,
  Receipt,
  Globe2,
} from "lucide-react";

import { usePageMeta } from "../hooks/usePageMeta";

const categories = [
  {
    name: "Initial evaluations",
    usd: "$350",
    eur: "€300",
    note: "60–90 min. Full clinical report on request.",
  },
  {
    name: "Therapy sessions (Aphasia · Dysarthria · Cognitive · Voice · Dysphagia)",
    usd: "$225",
    eur: "€140",
    note: "Standard 50–60 minute session.",
  },
  {
    name: "Mealtime / vocal hygiene coaching",
    usd: "$175",
    eur: "€120",
    note: "Caregiver and family training, 45 minutes.",
  },
];

const programmes = [
  {
    name: "SPEAK OUT!® — full 12-session course",
    usd: "$2,200",
    eur: "€1,800",
    note: "3 sessions/week for 4 weeks.",
  },
  {
    name: "Aphasia / cognitive-communication 8-week block",
    usd: "$1,600",
    eur: "€1,200",
    note: "1× weekly + between-session home practice, reassessed at 4 weeks.",
  },
  {
    name: "Accent & communication coaching · 6-session package",
    usd: "$1,150",
    eur: "€850",
    note: "Often pre-paid; elective service (not insurance-billable).",
  },
];

const Header = () => (
  <header className="border-b border-black/5 bg-[#FDFCF8]/90 backdrop-blur-xl sticky top-0 z-40">
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
      <Link to="/" data-testid="fees-back-home" className="flex items-center">
        <img
          src="/logo.jpg"
          alt="Neurological Communication Partners"
          className="h-14 w-auto rounded-xl ring-1 ring-black/5"
        />
      </Link>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-[#5C6661] hover:text-[#2D332F] transition"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Home
      </Link>
    </div>
  </header>
);

const PriceRow = ({ name, usd, eur, note }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 py-6 border-t border-black/10">
    <div className="md:col-span-6">
      <p className="font-medium text-[#2D332F] text-base md:text-lg leading-snug">
        {name}
      </p>
      <p className="text-sm text-[#5C6661] mt-1.5 leading-relaxed">{note}</p>
    </div>
    <div className="md:col-span-3 flex md:block items-baseline gap-3">
      <p className="text-xs uppercase tracking-[0.18em] text-[#5C6661] md:mb-1">
        USA
      </p>
      <p className="font-serif text-xl text-[#2D332F]">{usd}</p>
    </div>
    <div className="md:col-span-3 flex md:block items-baseline gap-3">
      <p className="text-xs uppercase tracking-[0.18em] text-[#5C6661] md:mb-1">
        Ireland
      </p>
      <p className="font-serif text-xl text-[#2D332F]">{eur}</p>
    </div>
  </div>
);

export default function Fees() {
  usePageMeta({
    title: "Fees & Private-Pay Pricing",
    description:
      "Transparent private-pay fees in USD and EUR. Initial evaluations, therapy sessions and SPEAK OUT!® packages. Superbills for out-of-network reimbursement (USA) and Med 2 for Irish tax relief.",
    path: "/fees",
  });
  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2D332F]">
      <Header />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
          Fees & pricing
        </p>
        <h1
          data-testid="fees-title"
          className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]"
        >
          Transparent, private-pay
          <br />
          <em className="text-[#739072]">therapy.</em>
        </h1>
        <p className="mt-6 text-[#5C6661] text-lg leading-relaxed max-w-2xl">
          Standard fees for adult clinical services. Final scope is confirmed
          during your free 15-minute discovery call, so you always know what
          you&rsquo;re committing to before the first session.
        </p>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-14"
          data-testid="fees-sessions"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#739072] mb-4">
            Sessions &amp; evaluations
          </p>
          <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F] mb-2">
            Per-session pricing
          </h2>
          <p className="text-[#5C6661] mb-6">
            Telehealth and in-person rates are the same.
          </p>
          <div>
            {categories.map((c) => (
              <PriceRow key={c.name} {...c} />
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20"
          data-testid="fees-programmes"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#739072] mb-4">
            Structured programmes
          </p>
          <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F] mb-2">
            Multi-session packages
          </h2>
          <p className="text-[#5C6661] mb-6">
            Pre-paid blocks reflect a small discount vs. session-by-session
            billing.
          </p>
          <div>
            {programmes.map((p) => (
              <PriceRow key={p.name} {...p} />
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          <div
            data-testid="fees-info-receipts"
            className="rounded-3xl border border-black/5 bg-white p-7"
          >
            <Receipt
              size={20}
              strokeWidth={1.5}
              className="text-[#739072] mb-3"
            />
            <p className="font-serif text-xl text-[#2D332F] mb-2">
              Receipts &amp; reimbursement
            </p>
            <ul className="text-sm text-[#2D332F]/85 leading-relaxed space-y-2">
              <li className="flex gap-2">
                <CheckCircle2
                  size={14}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                <span>
                  <strong>USA</strong> — itemised superbills provided for
                  out-of-network insurance reimbursement.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2
                  size={14}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                <span>
                  <strong>Ireland</strong> — Med 2 form provided for tax
                  relief at the standard rate.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2
                  size={14}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                <span>
                  Accent &amp; communication coaching is an elective service
                  and is not insurance-billable in either jurisdiction.
                </span>
              </li>
            </ul>
          </div>

          <div
            data-testid="fees-info-currency"
            className="rounded-3xl border border-black/5 bg-white p-7"
          >
            <Globe2
              size={20}
              strokeWidth={1.5}
              className="text-[#739072] mb-3"
            />
            <p className="font-serif text-xl text-[#2D332F] mb-2">
              Currency &amp; payment
            </p>
            <ul className="text-sm text-[#2D332F]/85 leading-relaxed space-y-2">
              <li className="flex gap-2">
                <CheckCircle2
                  size={14}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                <span>
                  USA clients invoiced in USD; Ireland/EU clients invoiced in
                  EUR.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2
                  size={14}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                <span>
                  Payment details are <strong>shared securely after booking</strong>
                  {" "}— never published here, to protect both you and the practice.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2
                  size={14}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                <span>
                  Card, bank transfer or Revolut payment link accepted. Due
                  before each session or at the start of a pre-paid block.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2
                  size={14}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                <span>
                  Sliding-scale and hardship considerations are discussed
                  one-to-one — never publicly priced.
                </span>
              </li>
            </ul>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 rounded-3xl border border-black/5 bg-[#F3F1E7] p-8 md:p-12"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-3">
            Free 15-minute discovery call
          </p>
          <p className="font-serif text-2xl md:text-3xl font-light leading-snug text-[#2D332F] mb-5 max-w-2xl">
            Before any fee changes hands, we&rsquo;ll have a short, honest
            conversation about whether therapy is the right fit — and what
            it would actually cost you.
          </p>
          <Link
            to="/#contact"
            data-testid="fees-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm hover:bg-[#739072] transition"
          >
            Request your discovery call
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </motion.section>

        <p className="mt-10 text-xs text-[#5C6661] max-w-3xl">
          Prices last reviewed {new Date().toLocaleDateString("en-IE", {
            year: "numeric",
            month: "long",
          })}
          . Final fees are confirmed in writing before therapy begins.
        </p>
      </main>
    </div>
  );
}
